import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AddCatalogSizeRequest,
  CatalogAddressPlanModel,
  CatalogAddressResourceService,
  CatalogAffectedModel,
  CatalogAssignResult,
  CatalogCardCreatedModel,
  CatalogCardModel,
  CatalogCardResourceService,
  CatalogIssueResultModel,
  CatalogMeModel,
  CatalogSectionModel,
  CatalogSectionResourceService,
  CatalogSlugHintModel,
  CatalogSummaryModel,
  CatalogUncoveredCountModel,
  CatalogUncoveredModel,
  CatalogUncoveredResourceService,
  PagedCatalogCardModel,
  SaveCatalogSectionRequest,
  SaveResult,
  SaveEntryRequest,
  UiLabel,
} from '../vn-api';
import { HxDictionaryService } from './dictionary.service';

/** The key of the dictionary «Метки товара»: an entry code is a tag of a composite product card. */
export const PRODUCT_TAGS_DICTIONARY_KEY = 'product-tags';

/** The filter of the cards list of the catalog (`filter` of `GET /adm/catalog/cards`). */
export type CatalogCardFilter = 'all' | 'no_section' | 'no_address' | 'with_address';

/** A label of a product card = an entry of the dictionary `product-tags`; `code` is the tag stored in `tags_` of the card. */
export interface ProductLabel {
  code: string;
  title: UiLabel;
  /** `#RRGGBB`, kept in `attributes.color` of the entry */
  color?: string;
  seq?: number;
  enabled: boolean;
  /** the other attributes of the entry: kept as they are when the set is saved */
  attributes?: { [key: string]: unknown };
}

export interface ProductLabelSet {
  /** empty while the dictionary does not exist yet */
  dictionaryId?: number;
  labels: ProductLabel[];
}

/**
 * The catalog of the site seen from adm: sections of composite products, the cards with section and address, the addresses of the cake pages, the products without a card
 * and the dictionary of labels. Reading is for every signed-in employee, writing is for the admin (the backend decides; `getMe()` tells the screen what to show).
 */
@Injectable({
  providedIn: 'root'
})
export class HxCatalogService {
  constructor(
    private sectionResource: CatalogSectionResourceService,
    private cardResource: CatalogCardResourceService,
    private addressResource: CatalogAddressResourceService,
    private uncoveredResource: CatalogUncoveredResourceService,
    private dictionaryService: HxDictionaryService,
  ) {
  }

  getMe(): Promise<CatalogMeModel> {
    return firstValueFrom(this.cardResource.getCatalogMe());
  }

  getSummary(countryId?: number): Promise<CatalogSummaryModel> {
    return firstValueFrom(this.cardResource.getCatalogSummary(undefined, countryId));
  }

  // ---------------------------------------------------------------- sections

  getSections(params?: { countryId?: number; includeRemoved?: boolean }): Promise<CatalogSectionModel[]> {
    return firstValueFrom(this.sectionResource.getCatalogSections(undefined, params?.countryId, params?.includeRemoved));
  }

  getSectionSlugHint(countryId: number, title: string): Promise<CatalogSlugHintModel> {
    return firstValueFrom(this.sectionResource.getCatalogSectionSlugHint(countryId, undefined, title));
  }

  createSection(req: SaveCatalogSectionRequest): Promise<SaveResult> {
    return firstValueFrom(this.sectionResource.createCatalogSection(req));
  }

  updateSection(id: number, req: SaveCatalogSectionRequest): Promise<unknown> {
    return firstValueFrom(this.sectionResource.updateCatalogSection(id, req));
  }

  setSectionEnabled(id: number, value: boolean): Promise<unknown> {
    return firstValueFrom(this.sectionResource.setCatalogSectionEnabled(id, {value}));
  }

  /** `seq` of the sections of the country becomes 10, 20, 30… in the order of `ids`. */
  reorderSections(countryId: number, ids: number[]): Promise<unknown> {
    return firstValueFrom(this.sectionResource.reorderCatalogSections({countryId, ids}));
  }

  publishAllSections(countryId: number): Promise<CatalogAffectedModel> {
    return firstValueFrom(this.sectionResource.publishAllCatalogSections({countryId}));
  }

  unpublishAllSections(countryId: number): Promise<CatalogAffectedModel> {
    return firstValueFrom(this.sectionResource.unpublishAllCatalogSections({countryId}));
  }

  removeSection(id: number): Promise<unknown> {
    return firstValueFrom(this.sectionResource.removeCatalogSection(id));
  }

  // ---------------------------------------------------------------- cards

  getCards(params?: {
    countryId?: number;
    sectionId?: number;
    filter?: CatalogCardFilter;
    search?: string;
    ids?: number[];
    page?: number;
    limit?: number;
  }): Promise<PagedCatalogCardModel> {
    return firstValueFrom(this.cardResource.getCatalogCards(params?.countryId, params?.filter, params?.ids, params?.limit, params?.page, params?.search, params?.sectionId));
  }

  getCardById(id: number): Promise<CatalogCardModel> {
    return firstValueFrom(this.cardResource.getCatalogCardById(id));
  }

  /** One card into a section (`undefined` = out of any section). Answers `catalog.card.noAddress` when the card cannot get an address. */
  setCardSection(id: number, sectionId?: number): Promise<CatalogAssignResult> {
    return firstValueFrom(this.cardResource.setCatalogCardSection(id, {sectionId}));
  }

  /** «В раздел…» for many cards; the cards that cannot get an address come back in `skipped`, the others are done. */
  assignCardsToSection(compositeProductIds: number[], sectionId?: number): Promise<CatalogAssignResult> {
    return firstValueFrom(this.cardResource.assignCatalogCardsToSection({compositeProductIds, sectionId}));
  }

  // ---------------------------------------------------------------- addresses

  previewAddresses(countryId?: number): Promise<CatalogAddressPlanModel> {
    return firstValueFrom(this.addressResource.getCatalogAddressPreview(countryId));
  }

  issueAddresses(countryId?: number): Promise<CatalogIssueResultModel> {
    return firstValueFrom(this.addressResource.issueCatalogAddresses({countryId}));
  }

  // ---------------------------------------------------------------- products without a card

  getUncovered(countryId?: number): Promise<CatalogUncoveredModel> {
    return firstValueFrom(this.uncoveredResource.getCatalogUncovered(countryId));
  }

  getUncoveredCount(countryId?: number): Promise<CatalogUncoveredCountModel> {
    return firstValueFrom(this.uncoveredResource.getCatalogUncoveredCount(countryId));
  }

  createCardFromProduct(countryId: number, productInfoId: number): Promise<CatalogCardCreatedModel> {
    return firstValueFrom(this.uncoveredResource.createCatalogCardFromProduct({countryId, productInfoId}));
  }

  addProductAsCardSize(req: AddCatalogSizeRequest): Promise<CatalogCardCreatedModel> {
    return firstValueFrom(this.uncoveredResource.addCatalogProductAsCardSize(req));
  }

  // ---------------------------------------------------------------- the dictionary «Метки товара»

  /** The labels with the disabled ones, in the order of the dictionary. */
  async getProductLabels(): Promise<ProductLabelSet> {
    const dictionaryId = await this.findProductLabelsDictionaryId();
    if (dictionaryId === undefined) {
      return {labels: []};
    }
    const entries = await this.dictionaryService.getEntries({key: PRODUCT_TAGS_DICTIONARY_KEY});
    const labels: ProductLabel[] = entries.map(entry => {
      const {color, ...rest} = (entry.attributes ?? {}) as { [key: string]: unknown };
      return {
        code: entry.code,
        title: entry.title,
        color: typeof color === 'string' ? color : undefined,
        seq: entry.seq,
        enabled: entry.enabled,
        attributes: Object.keys(rest).length > 0 ? rest : undefined,
      };
    });
    return {dictionaryId, labels};
  }

  /**
   * Saves the COMPLETE set of labels: the backend replaces the entries of the dictionary with this list, so an entry that is not sent is deleted.
   * The dictionary is created when it does not exist yet (the admin is the only one who gets here).
   */
  async saveProductLabels(set: ProductLabelSet, labels: ProductLabel[]): Promise<ProductLabelSet> {
    const dictionaryId = set.dictionaryId ?? (await this.dictionaryService.saveDictionary({
      key: PRODUCT_TAGS_DICTIONARY_KEY,
      title: {ru: 'Метки товара', en: 'Product labels'},
      description: 'Метки карточек на сайте: код = тег карточки, название на языках, цвет (attributes.color), порядок',
      enabled: true,
    })).id;
    const entries: SaveEntryRequest[] = labels.map((label, ix) => ({
      enabled: label.enabled,
      title: label.title,
      code: label.code.trim(),
      seq: ix + 1,
      attributes: label.color ? {...(label.attributes ?? {}), color: label.color} : label.attributes,
      childEntries: [],
    }));
    await this.dictionaryService.saveEntries(dictionaryId, entries);
    return this.getProductLabels();
  }

  private async findProductLabelsDictionaryId(): Promise<number | undefined> {
    const found = await this.dictionaryService.getDictionaryList({query: PRODUCT_TAGS_DICTIONARY_KEY, limit: 100});
    return found.list.find(d => d.key === PRODUCT_TAGS_DICTIONARY_KEY)?.id;
  }
}
